import { _getLogger } from '@doxyme/analytics';
import { Specs } from '@doxyme/extensions-common';
import ExtensionCapability from '../../capability';
import type Instance from '../../instance';

const logger = _getLogger('play-sound', {
  component: 'extensions',
  lib: 'bridge',
});

type Constraints = Specs.PlaySound.Constraints;
type PlayPayload = Specs.PlaySound.PlayPayload;

const { PlaySound } = Specs;

const DEFAULT_VOLUME = 0.7; // 70% volume by default
const DEFAULT_PAN = 0; // pan 0 (center) by default
const IDLE_TIMEOUT = 10000; // 10s of idle time before closing the audio context

/**
 * PlaySound allows extensions to play a sound within the host application.
 */
export default class PlaySoundCapability extends ExtensionCapability<Constraints> {
  static readonly id = PlaySound.id;

  private audioContext?: AudioContext;
  private idleTimeout?: NodeJS.Timeout;

  constructor() {
    super(PlaySound);

    this.handlers.push([PlaySound.Actions.Play, this.#play.bind(this)]);
  }

  /**
   * Get or create an audio context
   */
  #getOrCreateAudioContext(): AudioContext {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.audioContext = new AudioContext();
    }
    return this.audioContext;
  }

  /**
   * Schedule cleanup of the audio context after a period of inactivity
   */
  #scheduleCleanup(): void {
    clearTimeout(this.idleTimeout);
    this.idleTimeout = setTimeout(async () => {
      if (this.audioContext && this.audioContext.state !== 'closed') {
        await this.audioContext.close();
      }
    }, IDLE_TIMEOUT);
  }

  /**
   * "Play" action handler, plays a sound
   */
  async #play(
    payload: PlayPayload,
    instance: Instance<Constraints>,
  ): Promise<void> {
    const { source: arrayBuffer, config } = payload;
    const { pan, volume } = config ?? {};

    const audioContext = this.#getOrCreateAudioContext();
    try {
      // resume the audio context if it is suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // decode the audio data
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      // create nodes
      const sourceNode = audioContext.createBufferSource();
      sourceNode.buffer = audioBuffer;

      const gainNode = audioContext.createGain();
      const panner = audioContext.createStereoPanner();

      // configure volume and pan
      gainNode.gain.value = volume ?? DEFAULT_VOLUME;
      panner.pan.value = pan ?? DEFAULT_PAN;

      // connect the nodes
      sourceNode.connect(gainNode);
      gainNode.connect(panner);
      panner.connect(audioContext.destination);

      sourceNode.onended = () => {
        instance.notify([PlaySound.Events.PlayStatus, { isSuccess: true }]);

        // disconnect the nodes
        sourceNode.disconnect();
        gainNode.disconnect();
        panner.disconnect();

        this.#scheduleCleanup();
      };

      // play the sound
      sourceNode.start();
    } catch (error) {
      logger.error('Failed to play sound', {
        error,
        manifest: { id: instance.manifest.id, name: instance.manifest.name },
        action: PlaySound.Actions.Play,
      });
      instance.notify([
        PlaySound.Events.PlayStatus,
        {
          isSuccess: false,
          errorMessage:
            error instanceof Error ? error.message : 'Failed to play sound',
        },
      ]);
    }
  }

  /**
   * Clean up resources when the capability is cleared
   * @hidden
   */
  override clear(): void {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = undefined;
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = undefined;
    }
    super.clear();
  }
}
